import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { adminAPI } from '../../api';
import LoadingSpinner from '../LoadingSpinner';
import { toast } from 'sonner';
import { Download, Search, Ban, CheckCircle } from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAllUsers();
      setUsers(res.data || []);
    } catch (e) {
      toast.error('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleBan = async (user) => {
    const isBanned = !!user.banned;
    const reason = isBanned ? '' : (window.prompt('Motivo del ban:') || 'No especificado');
    if (!isBanned && reason === null) return;
    try {
      await adminAPI.banUser(user.id, { banned: !isBanned, reason });
      toast.success(`Usuario ${!isBanned ? 'baneado' : 'desbaneado'} correctamente`);
      load();
    } catch (e) {
      toast.error('Error procesando acción');
    }
  };

  const exportCSV = async () => {
    try {
      const res = await adminAPI.exportCSV('users');
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error exportando CSV');
    }
  };

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="py-12 flex justify-center"><LoadingSpinner /></div>;

  return (
    <Card data-testid="admin-users-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Gestión de Usuarios ({users.length})</CardTitle>
        <Button onClick={exportCSV} variant="outline" size="sm" data-testid="export-users-csv-btn">
          <Download className="w-4 h-4 mr-2" /> Exportar CSV
        </Button>
      </CardHeader>
      <CardContent>
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <Input
            placeholder="Buscar por nombre o email..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="users-search-input"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-3">Nombre</th>
                <th className="p-3">Email</th>
                <th className="p-3">Reservas</th>
                <th className="p-3">Estado</th>
                <th className="p-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t" data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-medium">{u.name}</td>
                  <td className="p-3 text-gray-600">{u.email}</td>
                  <td className="p-3">{u.bookings_count || 0}</td>
                  <td className="p-3">
                    {u.banned ? (
                      <Badge variant="destructive">Baneado</Badge>
                    ) : (
                      <Badge className="bg-green-600">Activo</Badge>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      size="sm"
                      variant={u.banned ? 'default' : 'destructive'}
                      onClick={() => toggleBan(u)}
                      data-testid={`ban-toggle-btn-${u.id}`}
                    >
                      {u.banned ? <><CheckCircle className="w-3 h-3 mr-1" /> Desbanear</> : <><Ban className="w-3 h-3 mr-1" /> Banear</>}
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminUsers;
